
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, MessageCircle, Check } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Real form submit via Web3Forms — no backend, no MongoDB, no API keys to hide.
// Visitor types in fields → clicks Send → email lands in your inbox.
const WEB3FORMS_KEY = '8cd0e0ed-15df-4f32-9f62-598a231718f8';
const WHATSAPP_NUMBER = '19706378362'; // international, digits only

const GetTemplateModal = ({ template, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const buildBody = () => [
    `Hi Horizon team,`,
    ``,
    `I'd like to know more about the "${template.name}" template (${template.id}, ${template.price}).`,
    `Industry: ${template.industry}`,
    company ? `Company / project: ${company}` : null,
    name ? `My name: ${name}` : null,
    email ? `Email: ${email}` : null,
    ``,
    `What I'd like changed or added before buying:`,
    note || `(I'll share more once we connect.)`,
    ``,
    `Thanks!`,
  ].filter(Boolean).join('\n');

  const subject = `Template Inquiry — ${template.name} (${template.price})`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error(t('common.name_email_required'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject,
          from_name: `Horizon Template Inquiry — ${template.name}`,
          name,
          email,
          company,
          template: `${template.name} (${template.id})`,
          tier: template.tier === 'premium' ? '◆ Premium' : '✦ Signature',
          price: template.price,
          message: buildBody(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true);
      } else {
        toast.error(t('common.could_not_send'));
      }
    } catch {
      toast.error(t('common.could_not_send'));
    } finally {
      setSending(false);
    }
  };

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildBody())}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1816]/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="get-template-modal"
      >
        <Toaster position="top-center" richColors />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-2xl bg-[#FAF8F5] shadow-2xl max-h-[92vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header strip */}
          <div className="bg-[#1A1816] text-[#FAF8F5] px-6 py-3 flex items-center justify-between font-mono text-[10px] tracking-[0.32em] uppercase">
            <span>— Inquiry — {template.tier === 'premium' ? '◆ Premium' : '✦ Signature'} · {template.price}</span>
            <button onClick={onClose} data-testid="modal-close" className="text-[#FAF8F5]/60 hover:text-[#C9A96E]"><X className="w-4 h-4" /></button>
          </div>

          {!done ? (
            <form onSubmit={handleSubmit} className="p-7 sm:p-10">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#C9A96E]">— {t('common.contact_about_template')}</p>
              <h3 className="font-serif italic text-3xl sm:text-5xl text-[#1A1816] mt-2 leading-[1.05]">{template.name}</h3>
              <p className="text-sm text-[#8A857D] mt-2">{template.industry} · 4 pages · React + Tailwind + framer-motion</p>

              <p className="font-serif text-[15px] text-[#1A1816]/80 mt-6 leading-[1.7] border-l-2 border-[#C9A96E] pl-4">
                {t('common.tell_us')}
                <span className="block mt-2 text-[#1A1816] font-semibold">{t('common.no_payment')}</span>
              </p>

              <div className="grid grid-cols-2 gap-x-5 gap-y-5 mt-7">
                <Field label={t('common.your_name')} testId="modal-name" value={name} onChange={setName} required />
                <Field label={t('common.email')} type="email" testId="modal-email" value={email} onChange={setEmail} required />
                <Field label={t('common.company_project')} testId="modal-company" value={company} onChange={setCompany} className="col-span-2" />
                <div className="col-span-2">
                  <label className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#1A1816]/65 block mb-2">{t('common.what_to_change')}</label>
                  <textarea data-testid="modal-note" rows={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('common.what_to_change_ph')} className="w-full bg-transparent border border-[#1A1816]/20 px-3 py-2.5 outline-none focus:border-[#C9A96E] resize-none font-serif text-base text-[#1A1816]" />
                </div>
              </div>

              <div className="mt-7 pt-5 border-t border-[#1A1816]/10">
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#8A857D] mb-4">
                  {t('common.listed_price')} · <span className="text-[#1A1816] font-serif normal-case tracking-normal text-2xl ml-2">{template.price}</span>
                  <span className="block mt-1 text-[#8A857D] normal-case tracking-wide text-[11px]">{t('common.final_quote')}</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="modal-submit"
                    className="group flex items-center justify-center gap-2.5 bg-[#1A1816] text-[#FAF8F5] px-6 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#C9A96E] hover:text-[#1A1816] transition-colors disabled:opacity-60"
                  >
                    {sending ? (
                      <span>{t('common.sending')}</span>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        <span>{t('common.send_inquiry')}</span>
                      </>
                    )}
                  </button>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="modal-whatsapp"
                    className="group flex items-center justify-center gap-2.5 bg-[#25D366] text-white px-6 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#1FB855] transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>{t('common.whatsapp')}</span>
                  </a>
                </div>
              </div>
            </form>
          ) : (
            <div className="p-10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#C9A96E]/15 flex items-center justify-center"><Check className="w-7 h-7 text-[#C9A96E]" /></div>
              <h3 className="font-serif italic text-3xl text-[#1A1816] mt-5">{t('common.thank_you')}, {name.split(' ')[0]}.</h3>
              <p className="font-serif text-base text-[#8A857D] mt-3 max-w-md mx-auto leading-[1.7]">
                {t('common.inquiry_sent', { name: template.name })}
              </p>
              <button onClick={onClose} data-testid="modal-done" className="mt-7 bg-[#1A1816] text-[#FAF8F5] px-7 py-3 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#C9A96E] hover:text-[#1A1816] transition-colors">{t('common.close')}</button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function Field({ label, type = 'text', testId, value, onChange, required = false, className = '' }) {
  return (
    <div className={className}>
      <label className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#1A1816]/65 block mb-2">{label}{required && <span className="text-[#C9A96E] ml-1">*</span>}</label>
      <input type={type} data-testid={testId} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-[#1A1816]/30 px-1 py-2 outline-none focus:border-[#C9A96E] font-serif text-base text-[#1A1816]" />
    </div>
  );
}

export default GetTemplateModal;
